import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  CircularProgress,
  Collapse,
  IconButton,
  Chip,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { Link } from "react-router-dom";
import moment, { Moment } from "moment";
import SuggestedEventsService, {
  TripWaitSummaryReport,
  TripWaitSummary,
  WaitAction,
} from "services/suggestedEventsService";

const formatDuration = (minutes: number): string => {
  if (minutes >= 60) {
    const hours = minutes / 60;
    return `${hours.toFixed(1)} hr`;
  }
  return `${minutes.toFixed(0)} min`;
};

interface TripRowProps {
  trip: TripWaitSummary;
  formatDateTime: (dateStr: string) => string;
}

const TripRow: React.FC<TripRowProps> = ({ trip, formatDateTime }) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <TableRow
        sx={{ "& > *": { borderBottom: "unset" }, cursor: "pointer" }}
        onClick={() => setOpen(!open)}
        hover
      >
        <TableCell>
          <IconButton size="small">
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Link
            to={`/trips/${trip.tripId}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
          >
            {trip.tripId}
          </Link>
        </TableCell>
        <TableCell>{trip.controlNumber || "-"}</TableCell>
        <TableCell>{trip.clientName}</TableCell>
        <TableCell>{trip.hubName}</TableCell>
        <TableCell>{trip.tripDate ? formatDateTime(trip.tripDate) : "-"}</TableCell>
        <TableCell align="right">
          {trip.existingWaitCount} ({trip.existingWaitMinutes.toFixed(0)} min)
        </TableCell>
        <TableCell align="right">
          {trip.suggestedWaitCount} ({trip.suggestedWaitMinutes.toFixed(0)} min)
        </TableCell>
        <TableCell align="right">
          <Chip
            label={`${trip.acceptedWaitCount} (${trip.acceptedWaitMinutes.toFixed(0)} min)`}
            color="success"
            size="small"
            variant="outlined"
          />
        </TableCell>
        <TableCell align="right">
          <Chip
            label={`${trip.dismissedWaitCount} (${trip.dismissedWaitMinutes.toFixed(0)} min)`}
            color="error"
            size="small"
            variant="outlined"
          />
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={10}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Actions
              </Typography>
              {trip.actions.length > 0 ? (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Action</TableCell>
                      <TableCell>User</TableCell>
                      <TableCell>Action Time</TableCell>
                      <TableCell>Suggested Time</TableCell>
                      <TableCell>Duration</TableCell>
                      <TableCell>Location</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {trip.actions.map((action: WaitAction, idx: number) => (
                      <TableRow key={idx}>
                        <TableCell>
                          <Chip
                            label={action.actionType}
                            color={action.actionType === "Accepted" ? "success" : "error"}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{action.userName || action.userEmail}</TableCell>
                        <TableCell>{formatDateTime(action.actionDateTimeUtc)}</TableCell>
                        <TableCell>{formatDateTime(action.suggestedStartTimeUtc)}</TableCell>
                        <TableCell>{action.durationMinutes.toFixed(1)} min</TableCell>
                        <TableCell>{action.locationName || "-"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <Typography variant="body2" color="textSecondary">
                  No actions recorded for this trip.
                </Typography>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const SuggestedEventsReport: React.FC = () => {
  const [report, setReport] = useState<TripWaitSummaryReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState<Moment>(moment().subtract(30, "days"));
  const [endDate, setEndDate] = useState<Moment>(moment());
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await SuggestedEventsService.getTripWaitSummary({
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        offset: page * rowsPerPage,
        pageSize: rowsPerPage,
      });
      setReport(result);
    } catch (error) {
      console.error("Failed to fetch trip wait summary:", error);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, page, rowsPerPage]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handlePageChange = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatDateTime = (dateStr: string) => {
    try {
      return moment(dateStr).format("MM/DD/YYYY HH:mm");
    } catch {
      return dateStr;
    }
  };

  const totals = report?.totals;

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3 }}>
        Trip Wait Summary Report
      </Typography>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={(date) => date && setStartDate(date)}
                slotProps={{ textField: { size: "small" } }}
              />
            </Grid>
            <Grid item>
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={(date) => date && setEndDate(date)}
                slotProps={{ textField: { size: "small" } }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Summary Totals */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      ) : totals ? (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={2}>
            <Card>
              <CardContent>
                <Typography variant="subtitle2" color="textSecondary">
                  Total Trips
                </Typography>
                <Typography variant="h4">{totals.totalTrips}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card>
              <CardContent>
                <Typography variant="subtitle2" color="textSecondary">
                  Existing Waits
                </Typography>
                <Typography variant="h5">{totals.totalExistingWaits}</Typography>
                <Typography variant="body2" color="textSecondary">
                  {formatDuration(totals.totalExistingWaitMinutes)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card>
              <CardContent>
                <Typography variant="subtitle2" color="textSecondary">
                  Suggested Waits
                </Typography>
                <Typography variant="h5">{totals.totalSuggestedWaits}</Typography>
                <Typography variant="body2" color="textSecondary">
                  {formatDuration(totals.totalSuggestedWaitMinutes)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card sx={{ bgcolor: "success.light" }}>
              <CardContent>
                <Typography variant="subtitle2" color="success.contrastText">
                  Accepted Waits
                </Typography>
                <Typography variant="h5" color="success.contrastText">
                  {totals.totalAcceptedWaits}
                </Typography>
                <Typography variant="body2" color="success.contrastText">
                  {formatDuration(totals.totalAcceptedWaitMinutes)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card sx={{ bgcolor: "error.light" }}>
              <CardContent>
                <Typography variant="subtitle2" color="error.contrastText">
                  Dismissed Waits
                </Typography>
                <Typography variant="h5" color="error.contrastText">
                  {totals.totalDismissedWaits}
                </Typography>
                <Typography variant="body2" color="error.contrastText">
                  {formatDuration(totals.totalDismissedWaitMinutes)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card>
              <CardContent>
                <Typography variant="subtitle2" color="textSecondary">
                  Acceptance Rate
                </Typography>
                <Typography variant="h4">{(totals.acceptanceRate * 100).toFixed(1)}%</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      ) : null}

      {/* Trips Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Trips with Wait Data
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell>Trip ID</TableCell>
                <TableCell>Control #</TableCell>
                <TableCell>Client</TableCell>
                <TableCell>Hub</TableCell>
                <TableCell>Trip Date</TableCell>
                <TableCell align="right">Existing Waits</TableCell>
                <TableCell align="right">Suggested Waits</TableCell>
                <TableCell align="right">Accepted</TableCell>
                <TableCell align="right">Dismissed</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {report?.trips.map((trip) => (
                <TripRow key={trip.tripId} trip={trip} formatDateTime={formatDateTime} />
              ))}
              {(!report?.trips || report.trips.length === 0) && !loading && (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    No trips with wait data found for the selected date range.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={report?.totalCount || 0}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
          />
        </CardContent>
      </Card>
    </Box>
  );
};

export default SuggestedEventsReport;
